export interface CityScopeAttributionOptions {
  src?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  baseOrigin?: string;
  keepAbsolute?: boolean;
}

const DEFAULT_ORIGIN = 'https://www.city-scope.co.uk';
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/i;
const IPV4_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/;

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

export function cleanAttributionValue(value: string | null | undefined): string | undefined {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || EMAIL_PATTERN.test(raw) || IPV4_PATTERN.test(raw)) return undefined;

  const cleaned = raw
    .replace(/https?:\/\/[^/\s]+/g, '')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

  return cleaned || undefined;
}

export function buildCityScopeUtmUrl(rawUrl: string, options: CityScopeAttributionOptions = {}): string {
  const baseOrigin = options.baseOrigin || (
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : DEFAULT_ORIGIN
  );
  const input = String(rawUrl || '').trim() || '/';
  const startedAbsolute = isAbsoluteUrl(input);

  let parsed: URL;
  try {
    parsed = new URL(input, baseOrigin);
  } catch {
    parsed = new URL('/', baseOrigin);
  }

  const params: Array<[string, string | null | undefined]> = [
    ['src', options.src],
    ['utm_source', options.utmSource],
    ['utm_medium', options.utmMedium],
    ['utm_campaign', options.utmCampaign],
    ['utm_content', options.utmContent],
    ['utm_term', options.utmTerm],
  ];

  for (const [key, rawValue] of params) {
    const cleaned = cleanAttributionValue(rawValue);
    if (cleaned) parsed.searchParams.set(key, cleaned);
  }

  if (options.keepAbsolute || startedAbsolute) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export const CITYSCOPE_INTERNAL_CTA_ATTRIBUTION = {
  utmSource: 'cityscope',
  utmMedium: 'internal_cta',
} as const;
